import React, { useCallback, useEffect, useMemo, useState } from "react";

import "./TableRowDetailsModal.css";

import TableRowPdfButton from "../../TableRowPdfButton/TableRowPdfButton";
import DynamicButton from "../../../DynamicButton/DynamicButton";
import ConfirmDialog from "../../../ConfirmDialog/ConfirmDialog";

import FieldRenderer from "./FieldRenderer";

import { getValue } from "../../utils";
import { isHttpUrl } from "./utils/fileUtils";
import { googleMapsEmbedSrc } from "./utils/mapUtils";



const MEDIA_FIELD_TYPES = new Set([
  "imagelink",
  "signature",
  "geolocation",
]);

function hasRenderableImageValue(raw) {
  const values = Array.isArray(raw)
    ? raw
    : raw
      ? [raw]
      : [];

  return values.some(
    (value) =>
      typeof value === "string" &&
      isHttpUrl(value.trim()),
  );
}

function hasRenderableMedia(row, column) {
  if (!row || !column?.field) return false;

  const type = String(column?.type || "")
    .trim()
    .toLowerCase();

  const raw = getValue(row, column.field);

  if (type === "imagelink") {
    return hasRenderableImageValue(raw);
  }

  if (type === "signature") {
    return (
      typeof raw === "string" &&
      isHttpUrl(raw.trim())
    );
  }

  if (type === "geolocation") {
    return Boolean(googleMapsEmbedSrc(raw));
  }

  return false;
}

export default function TableRowDetailsModal({
  open,
  row,
  columns = [],
  onClose,
  onEdit,
  onDelete,
  title = "Details",
  onPrev,
  onNext,
  hasPrev = true,
  hasNext = true,
  loop = false,
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  const safeTitle = useMemo(() => String(title || "Details"), [title]);

  const pdfFilename = useMemo(() => {
    const cleanTitle = safeTitle.replace(/\s+/g, "_").toLowerCase();
    return `${cleanTitle}_${row?._id || row?.reportId || "record"}.pdf`;
  }, [safeTitle, row]);

const { mediaColumns, detailColumns } = useMemo(() => {
  const media = [];
  const details = [];

  columns.forEach((column, originalIndex) => {
    const entry = {
      column,
      originalIndex,
    };

    const type = String(column?.type || "")
      .trim()
      .toLowerCase();

    if (MEDIA_FIELD_TYPES.has(type)) {
      /*
       * A media column is included only when its current
       * row value can actually be rendered.
       *
       * Empty image, invalid URL and invalid location fields
       * are completely omitted.
       */
      if (hasRenderableMedia(row, column)) {
        media.push(entry);
      }

      return;
    }

    details.push(entry);
  });

  return {
    mediaColumns: media,
    detailColumns: details,
  };
}, [columns, row]);

const hasMedia = mediaColumns.length > 0;

  const handleKeyDown = useCallback(
    (event) => {
      if (!open || showConfirm) return;

      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowLeft" && (loop || hasPrev)) onPrev?.();
      if (event.key === "ArrowRight" && (loop || hasNext)) onNext?.();
    },
    [open, showConfirm, onClose, onPrev, onNext, hasPrev, hasNext, loop],
  );

  useEffect(() => {
    if (!open) return undefined;

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open || !row) return null;

  return (
    <div
      className="safety-row-details"
      role="dialog"
      aria-modal="true"
      aria-label={safeTitle}
    >
      <div
        className={[
          "safety-row-details__dialog",
          !hasMedia ? "safety-row-details__dialog--details-only" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="safety-row-details__header">
          <h3 className="safety-row-details__title">{safeTitle}</h3>

          <button
            type="button"
            className="safety-row-details__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* <div className="safety-row-details__content">
          <div className="safety-row-details__grid">
            {orderedColumns.map(({ column, originalIndex }) => (
              <FieldRenderer
                key={`${column.field}-${originalIndex}`}
                row={row}
                column={column}
                index={originalIndex}
              />
            ))}
          </div>
        </div> */}

        <div className="safety-row-details__content">
          <div
            className={[
              "safety-row-details__split-layout",
              !hasMedia ? "safety-row-details__split-layout--details-only" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {/* <aside
              className="safety-row-details__media-panel"
              aria-label="Images and location"
            >
              <div className="safety-row-details__media-header">
                <div>
                  <span className="safety-row-details__panel-eyebrow">
                    Evidence
                  </span>

                  <h4 className="safety-row-details__panel-title">
                    Images & Location
                  </h4>
                </div>

                <span className="safety-row-details__media-count">
                  {mediaColumns.length}
                </span>
              </div>

              <div className="safety-row-details__media-stack">
                {mediaColumns.length ? (
                  mediaColumns.map(({ column, originalIndex }) => (
                    <FieldRenderer
                      key={`${column.field}-${originalIndex}`}
                      row={row}
                      column={column}
                      index={originalIndex}
                    />
                  ))
                ) : (
                  <div className="safety-row-details__media-empty">
                    <span className="safety-row-details__media-empty-icon">
                      <i className="fa fa-image" aria-hidden="true" />
                    </span>

                    <strong>No visual evidence available</strong>

                    <p>No images or geotag were attached to this record.</p>
                  </div>
                )}
              </div>
            </aside> */}

            {hasMedia ? (
              <aside
                className="safety-row-details__media-panel"
                aria-label="Images and location"
              >
                <div className="safety-row-details__media-header">
                  <div>
                    <span className="safety-row-details__panel-eyebrow">
                      Evidence
                    </span>

                    <h4 className="safety-row-details__panel-title">
                      Images & Location
                    </h4>
                  </div>

                  <span className="safety-row-details__media-count">
                    {mediaColumns.length}
                  </span>
                </div>

                <div className="safety-row-details__media-stack">
                  {mediaColumns.map(({ column, originalIndex }) => (
                    <FieldRenderer
                      key={`${
                        column.field || column.key || column.name || "media"
                      }-${originalIndex}`}
                      row={row}
                      column={column}
                      index={originalIndex}
                    />
                  ))}
                </div>
              </aside>
            ) : null}

            <section
              className="safety-row-details__details-panel"
              aria-label="Record details"
            >
              <div className="safety-row-details__details-panel-header">
                <div>
                  <span className="safety-row-details__panel-eyebrow">
                    Record Information
                  </span>

                  <h4 className="safety-row-details__panel-title">
                    {safeTitle}
                  </h4>
                </div>

                <span className="safety-row-details__details-count">
                  {detailColumns.length} fields
                </span>
              </div>

              <div className="safety-row-details__details-grid">
                {detailColumns.map(({ column, originalIndex }) => (
                  <FieldRenderer
                    key={`${column.field}-${originalIndex}`}
                    row={row}
                    column={column}
                    index={originalIndex}
                  />
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="safety-row-details__footer">
          {onDelete ? (
            <DynamicButton
              text="Delete"
              icon="fa-trash"
              size="md"
              variant="danger"
              bgColor="#ffffff"
              textColor="#b91c1c"
              outlineColor="#fecaca"
              hoverBgColor="#b91c1c"
              hoverTextColor="#ffffff"
              title="Delete this record"
              ariaLabel="Delete this record"
              onClick={() => setShowConfirm(true)}
            />
          ) : null}

          <div className="safety-row-details__spacer" />

          <TableRowPdfButton
            row={row}
            columns={columns}
            title={safeTitle}
            filename={pdfFilename}
            directFileDownload
            fileDownloadField={null}
            className="safety-row-details__pdf-button"
            size="md"
            variant="info"
            icon="fa-download"
          >
            Download
          </TableRowPdfButton>

          {onEdit ? (
            <DynamicButton
              text="Edit"
              icon="fa-edit"
              size="md"
              variant="outline"
              bgColor="#ffffff"
              textColor="#374151"
              outlineColor="#d1d5db"
              hoverBgColor="#111827"
              hoverTextColor="#ffffff"
              title="Edit this record"
              ariaLabel="Edit this record"
              onClick={onEdit}
            />
          ) : null}

          {onPrev || onNext ? (
            <div className="safety-row-details__nav-group">
              <DynamicButton
                icon="fa-arrow-left"
                size="sm"
                variant="outline"
                bgColor="#ffffff"
                textColor="#374151"
                outlineColor="#d1d5db"
                hoverBgColor="#111827"
                hoverTextColor="#ffffff"
                title="Previous record"
                ariaLabel="Previous record"
                onClick={() => (loop || hasPrev) && onPrev?.()}
                disabled={!loop && !hasPrev}
              />

              <DynamicButton
                icon="fa-arrow-right"
                size="sm"
                variant="outline"
                bgColor="#ffffff"
                textColor="#374151"
                outlineColor="#d1d5db"
                hoverBgColor="#111827"
                hoverTextColor="#ffffff"
                title="Next record"
                ariaLabel="Next record"
                onClick={() => (loop || hasNext) && onNext?.()}
                disabled={!loop && !hasNext}
              />
            </div>
          ) : null}

          <ConfirmDialog
            open={showConfirm}
            title="Delete this record?"
            message="Are you sure you want to delete this record? This action cannot be undone."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={() => {
              setShowConfirm(false);
              onDelete?.();
            }}
            onCancel={() => setShowConfirm(false)}
            variant="danger"
          />
        </div>
      </div>
    </div>
  );
}
